import React, { Suspense, lazy, useEffect } from 'react'
import { Router, Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { PrivateRoute } from '../helpers/PrivateRoute'
import { HelmetProvider } from 'react-helmet-async'
import AppHelmet from './App.helmet'

import history from './history'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { useDispatch, useSelector } from 'react-redux'
import { fetchUserPermissions } from '../redux/Admin.slice'

import './App.scss'
import './admin-ui-v2.scss'
import style from './AppStyle.module.scss'
import shellStyle from './admin-ui-v2.module.scss'
import './react-transition.css'
import 'antd/dist/antd.css'
import { Spin, Space } from 'antd'

import LoginAdmin from '../Admin/AdminLogin'
import Navigation from '../Admin/component/Navigation'
import { renderRoutes } from '../router/renderRoutes'
import { routes } from '../router/routes'
import AddCountry from '../Admin/AddCountry'
import CreateCountry from '../Admin/AddCountry/CreateCountry'
import ApplyingList from '../Admin/Applying/ApplyingList'
import CreateApply from '../Admin/Applying/CreateApply'
import CreateScholarship from '../Admin/Scholarship/CreateScholarship'
import ScholarshipList from '../Admin/Scholarship/ScholarshipList'
import CostList from '../Admin/Cost/CostList'
import CreateCost from '../Admin/Cost/CreateCost'
import EnglishScore from '../Admin/EnglishScore/EnglishScoreList'
import CreateEnglishScore from '../Admin/EnglishScore/CreateEnglishScore'
import ChineseScoreList from '../Admin/ChineseScore/ChineseScoreList'
import CreateChineseScore from '../Admin/ChineseScore/CreateChineseScore'
import Questionnaire from '../Admin/QuestionnaireList'
import FetchQuestionnaire from '../Admin/Questionnaire'
import ConfigList from '../Admin/Config/ConfigList'
import CreateConfig from '../Admin/Config/CreateConfig'
// import BlogCreate from '../Admin/CreateBlog'
// import BlogEdit from '../Blog/BlogEdit'
const AdminCreateCountry = lazy(() => import('../Admin/AddCountry/CreateCountry'))
const AdminReview = lazy(() => import('../Admin/AdminReview'))
const AdminListBlogs = lazy(() => import('../Admin/AdminListBlogs'))
const AdminListLesson = lazy(() => import('../Admin/AdminListLesson'))
const AdminListUser = lazy(() => import('../Admin/AdminListUser'))
const AdminListMajors = lazy(() => import('../Admin/AdminListMajors'))
const AdminCreateMajor = lazy(() => import('../Admin/AdminCreate/CreateMajor'))
const AdminProgram = lazy(() => import('../Admin/AdminListProgram.js'))
const AdminCreateProgram = lazy(() => import('../Admin/Program/CreateProgram'))
const AdminImportProgram = lazy(() => import('../Admin/Program/ImportProgram'))
// ADMIN
const AdminCreateBilling = lazy(() => import('../Admin/AdminCreate/CreateBilling'))
const CreateCourse = lazy(() => import('../Admin/AdminCreate/ManageCourse/CreateCourse'))
const ManageCourse = lazy(() => import('../Admin/AdminCreate/ManageCourse/ManageCourse'))
const CreateApplication = lazy(() => import('../Admin/AdminCreate/CreateApplication'))
const AdminListCourse = lazy(() => import('../Admin/AdminListCourse'))
const AdminUniversityListing = lazy(() => import('../Admin/AdminUniversityListing'))
const UniversityListingV3 = lazy(() => import('../Admin/AdminUniversityListing/UniversityListingV3'))
const ImportUniversity = lazy(() => import('../Admin/CreateUniversity/ImportUniversity'))
const AdminCreateUniversity = lazy(() => import('../Admin/CreateUniversity'))
const AdminListBilling = lazy(() => import('../Admin/AdminListBilling.js'))
const AdminListApplication = lazy(() => import('../Admin/AdminListApplication.js'))
const AdminCreateBlog = lazy(() => import('../Admin/CreateBlog'))
const AdminListBanner = lazy(() => import('../Admin/AdminListBanner.js'))
const AdminCreateBanner = lazy(() => import('../Admin/AdminCreate/CreateBanner'))
const AdminCreateAdmin = lazy(() => import('../Admin/AdminCreate/CreateAdmin'))
const AdminListAdmin = lazy(() => import('../Admin/AdminListAdmin.js'))
const ScholarshipListV2 = lazy(() => import('../Admin/InformationHub/ScholarshipList'))
const ScholarshipViewV2 = lazy(() => import('../Admin/InformationHub/ScholarshipView'))
const ScholarshipEditV2 = lazy(() => import('../Admin/InformationHub/ScholarshipEdit'))
const DocumentsV2 = lazy(() => import('../Admin/DocumentsV2'))
const CreateDocumentPage = lazy(() => import('../Admin/DocumentsV2/CreateDocumentPage'))
const DocumentFamilyPage = lazy(() => import('../Admin/DocumentsV2/DocumentFamilyPage'))
const CreateDocumentVariationPage = lazy(() => import('../Admin/DocumentsV2/DocumentFamilyPage').then((module) => ({ default: module.CreateDocumentVariationPage })))
const OptionsManagement = lazy(() => import('../Admin/OptionsManagement/OptionsManagement'))
const AdminManagementV2 = lazy(() => import('../Admin/ManagementV2/AdminManagementV2'))
const FileManager = lazy(() => import('../Admin/FileManager/FileManager'))
const AdminHome = lazy(() => import('../Admin/Home/Home'))
const UniversityDetail = lazy(() => import('../module/University/pages/UniversityDetail'))
const UniversityView = lazy(() => import('../module/University/pages/UniversityView'))
const StudentProfiles = lazy(() => import('../Admin/CRM/StudentProfiles'))
const MemberProfiles = lazy(() => import('../Admin/CRM/MemberProfiles'))
const MembershipInterests = lazy(() => import('../Admin/CRM/MembershipInterests'))

const ROUTE_PERMISSION = {
  UNIVERSITIES_READ: 'universities.read',
  UNIVERSITIES_READ_PUBLISHED: 'universities.read.published',
  UNIVERSITIES_UPDATE: 'universities.update',
  SCHOLARSHIPS_READ: 'scholarships.read',
  SCHOLARSHIPS_UPDATE: 'scholarships.update',
  DOCUMENTS_READ: 'documents.read',
  SYSTEM_OPTIONS_READ: 'system_options.read',
  LEGACY_MENU_ACCESS: 'legacy_menu.access',
  ADMINS_READ: 'admins.read',
  CRM_READ: 'crm.read',
  ADMISSION_READ: 'admission.read',
}

const TransitionSwitch = ({ children }) => {
  const location = useLocation()
  return (
    <TransitionGroup>
      <CSSTransition key={location.key} classNames='fade' timeout={300}>
        <Suspense
          fallback={
            <div className={style.spinnerWrapper}>
              <Space size='middle' className={style.spinner}>
                <Spin size='large' />
              </Space>
            </div>
          }
        >
          <Switch location={location}>{children}</Switch>
        </Suspense>
      </CSSTransition>
    </TransitionGroup>
  )
}

const isModernAdminRoute = (pathname) =>
  pathname.includes('/admin/v2/') ||
  pathname.includes('/admin/v3/')

function App() {
  useEffect(() => {
    const unlisten = history.listen(() => window.scrollTo(0, 0)) // Scroll to top!
    return () => {
      unlisten && unlisten()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  const dispatch = useDispatch()
  const auth = useSelector((state) => state.admin.auth)

  useEffect(() => {
    if ((auth && auth.token) || localStorage.getItem('token')) {
      dispatch(fetchUserPermissions())
    }
  }, [auth, dispatch])

  return (
    <HelmetProvider>
      <AppHelmet />
      <Router history={history}>
        <Switch>
          <Route exact path='/admin/login' component={LoginAdmin} />
          <Route path='/' render={({ location }) => (
            <div className={`${style.layoutAdmin} ${shellStyle.page}`}>
              <Navigation />
              <div
                className={[
                  style.coverLayout,
                  isModernAdminRoute(location.pathname) ? style.modernShell : style.legacyShell,
                  isModernAdminRoute(location.pathname) ? 'admin-ui-v2-scope' : '',
                  location.pathname.includes('/v2/') ? style.noPadding : '',
                  isModernAdminRoute(location.pathname) ? shellStyle.modernShell : shellStyle.legacyShell,
                ].filter(Boolean).join(' ')}
              >
                <TransitionSwitch>
                  {renderRoutes(routes)}
                  <PrivateRoute exact path='/admin/v2/home' component={AdminHome} />
                  <PrivateRoute exact path='/admin/university' component={AdminUniversityListing} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/v2/university' anyOf={[ROUTE_PERMISSION.UNIVERSITIES_READ, ROUTE_PERMISSION.UNIVERSITIES_READ_PUBLISHED]}>
                    <Redirect to='/admin/v3/university' />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/v3/university' component={UniversityListingV3} anyOf={[ROUTE_PERMISSION.UNIVERSITIES_READ, ROUTE_PERMISSION.UNIVERSITIES_READ_PUBLISHED]} />
                  <PrivateRoute exact path='/admin/v2/university/view/:id' component={UniversityView} anyOf={[ROUTE_PERMISSION.UNIVERSITIES_READ, ROUTE_PERMISSION.UNIVERSITIES_READ_PUBLISHED]} />
                  <PrivateRoute exact path='/admin/v2/university/edit/:id' component={UniversityDetail} permission={ROUTE_PERMISSION.UNIVERSITIES_UPDATE} />
                  <PrivateRoute exact path='/admin/university/new' component={AdminCreateUniversity} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/university/import' component={ImportUniversity} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />

                  <PrivateRoute exact path='/admin/v2/scholarships' component={ScholarshipListV2} permission={ROUTE_PERMISSION.SCHOLARSHIPS_READ} />
                  <PrivateRoute exact path='/admin/v2/scholarships/view/:id' component={ScholarshipViewV2} permission={ROUTE_PERMISSION.SCHOLARSHIPS_READ} />
                  <PrivateRoute exact path='/admin/v2/scholarships/edit/:id' component={ScholarshipEditV2} permission={ROUTE_PERMISSION.SCHOLARSHIPS_UPDATE} />
                  <PrivateRoute exact path='/admin/v2/documents' component={DocumentsV2} permission={ROUTE_PERMISSION.DOCUMENTS_READ} />
                  <PrivateRoute exact path='/admin/v2/documents/new' component={CreateDocumentPage} permission={ROUTE_PERMISSION.DOCUMENTS_READ} />
                  <PrivateRoute exact path='/admin/v2/documents/:documentId/variations/new' component={CreateDocumentVariationPage} permission={ROUTE_PERMISSION.DOCUMENTS_READ} />
                  <PrivateRoute exact path='/admin/v2/documents/:documentId' component={DocumentFamilyPage} permission={ROUTE_PERMISSION.DOCUMENTS_READ} />
                  <PrivateRoute exact path='/admin/v2/options-management' component={OptionsManagement} permission={ROUTE_PERMISSION.SYSTEM_OPTIONS_READ} />
                  <PrivateRoute exact path='/admin/v2/crm/student-profiles' component={StudentProfiles} permission={ROUTE_PERMISSION.ADMISSION_READ} />
                  <PrivateRoute exact path='/admin/v2/crm/member-profiles' component={MemberProfiles} permission={ROUTE_PERMISSION.CRM_READ} />
                  <PrivateRoute exact path='/admin/v2/crm/member-profiles/membership-inquiries' component={MembershipInterests} permission={ROUTE_PERMISSION.CRM_READ} />

                  <PrivateRoute exact path='/admin/program' component={AdminProgram} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/program/new' component={AdminCreateProgram} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/program/import' component={AdminImportProgram} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/program/edit/:programId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <AdminCreateProgram isEdit />
                  </PrivateRoute>

                  <PrivateRoute exact path='/admin/majors' component={AdminListMajors} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/majors/new' component={AdminCreateMajor} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/majors/edit/:majorId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <AdminCreateMajor isEdit />
                  </PrivateRoute>

                  <PrivateRoute exact path='/admin/university/edit/:universityId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <AdminCreateUniversity isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/courses' component={AdminListCourse} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/courses/new' component={CreateCourse} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/courses/edit/:courseId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateCourse isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/courses/manage/:courseId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <ManageCourse isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/applications' component={AdminListApplication} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/applications/new' component={CreateApplication} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/applications/edit/:applicationId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateApplication isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/lessons' component={AdminListLesson} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />

                  <PrivateRoute exact path='/admin/blogs' component={AdminListBlogs} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute path='/admin/blog/new' component={AdminCreateBlog} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute path='/admin/blog/edit/:blogId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <AdminCreateBlog isEdit />
                  </PrivateRoute>

                  <PrivateRoute exact path='/admin/homepic' component={AdminListBanner} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute path='/admin/homepic/new' component={AdminCreateBanner} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute path='/admin/homepic/edit/:bannerId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <AdminCreateBanner isEdit />
                  </PrivateRoute>

                  <PrivateRoute exact path='/admin/billings' component={AdminListBilling} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/billings/new' component={AdminCreateBilling} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute path='/admin/billing/edit/:billingId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <AdminCreateBilling isEdit />
                  </PrivateRoute>

                  <PrivateRoute exact path='/admin/users' component={AdminListUser} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/reviews' component={AdminReview} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />

                  <PrivateRoute exact path='/admin/managements/listadmin' component={AdminListAdmin} permission={ROUTE_PERMISSION.ADMINS_READ} />
                  <PrivateRoute exact path='/admin/managements/new' component={AdminCreateAdmin} permission={ROUTE_PERMISSION.ADMINS_READ} />
                  <PrivateRoute exact path='/admin/countries/new' component={AdminCreateCountry} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/countries' component={AddCountry} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute path='/admin/countries/edit/:countryId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateCountry isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/applying' component={ApplyingList} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/applying/new' component={CreateApply} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/applying/edit/:applyingId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateApply isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/scholarship' component={ScholarshipList} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/scholarship/new' component={CreateScholarship} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/scholarship/edit/:scholarshipId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateScholarship isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/cost' component={CostList} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/cost/new' component={CreateCost} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/cost/edit/:costId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateCost isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/english-score' component={EnglishScore} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/english-score/new' component={CreateEnglishScore} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/english-score/edit/' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateEnglishScore isEdit />
                  </PrivateRoute>
                  <PrivateRoute
                    exact
                    path='/admin/questionnaire/:questionnaireId'
                    component={FetchQuestionnaire}
                    permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}
                  />
                  <PrivateRoute exact path='/admin/questionnaire' component={Questionnaire} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/chinese-score' component={ChineseScoreList} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/chinese-score/new' component={CreateChineseScore} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/chinese-score/edit' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateChineseScore isEdit />
                  </PrivateRoute>
                  <PrivateRoute exact path='/admin/v2/management' component={AdminManagementV2} permission={ROUTE_PERMISSION.ADMINS_READ} />
                  <PrivateRoute exact path='/admin/v2/file-manager' component={FileManager} permission={ROUTE_PERMISSION.ADMINS_READ} />
                  <PrivateRoute exact path='/admin/config' component={ConfigList} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />

                  <PrivateRoute exact path='/admin/config/new' component={CreateConfig} permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS} />
                  <PrivateRoute exact path='/admin/config/edit/:configId' permission={ROUTE_PERMISSION.LEGACY_MENU_ACCESS}>
                    <CreateConfig isEdit />
                  </PrivateRoute>
                  <Redirect to='/admin/v2/home' />
                </TransitionSwitch>
              </div>
            </div>
          )} />
        </Switch>
      </Router>
    </HelmetProvider>
  )
}

export default App
